import { Button, type ButtonProps } from '@blueprintjs/core';
import { forwardRef, type ReactElement } from 'react';
import { ShortcutHint } from './ShortcutHint';
import type { KeyActions, KeyCombination } from '@/models/KeyMapModel';
import { AppTooltip } from './AppTooltip';

export type HintButtonProps = ButtonProps & {
  tooltipContent?: string | ReactElement;
  tooltipClassName?: string;
  shortcutAction?: KeyActions;
  shortcut?: KeyCombination;
};

/** Consumes legacy hint titles; never emits a competing browser-native tooltip. */
export const HintButton = forwardRef<HTMLButtonElement, HintButtonProps>(
  function HintButton(
    { title, tooltipContent, tooltipClassName, shortcutAction, shortcut, ...props },
    forwardedRef,
  ) {
    const content =
      tooltipContent ??
      title ??
      (!props.children && !props.text ? props['aria-label'] : undefined);
    if (!content) return <Button {...props} ref={forwardedRef} />;
    return (
      <AppTooltip
        portalClassName={tooltipClassName}
        content={
          shortcutAction || shortcut ? (
            <ShortcutHint action={shortcutAction} combination={shortcut}>
              {content}
            </ShortcutHint>
          ) : (
            content
          )
        }
        disabled={props['aria-expanded'] === true || props['aria-expanded'] === 'true'}
        openOnTargetFocus={!props.disabled}
        renderTarget={({ ref, isOpen: _isOpen, ...targetProps }) => (
          <Button
            {...targetProps}
            {...props}
            onPointerEnter={(event) => {
              if (props.disabled) targetProps.onMouseEnter?.(event);
              props.onPointerEnter?.(event);
            }}
            onPointerLeave={(event) => {
              if (props.disabled) targetProps.onMouseLeave?.(event);
              props.onPointerLeave?.(event);
            }}
            onFocus={(event) => {
              targetProps.onFocus?.(event);
              props.onFocus?.(event);
            }}
            onBlur={(event) => {
              targetProps.onBlur?.(event);
              props.onBlur?.(event);
            }}
            onMouseEnter={(event) => {
              targetProps.onMouseEnter?.(event);
              props.onMouseEnter?.(event);
            }}
            onMouseLeave={(event) => {
              targetProps.onMouseLeave?.(event);
              props.onMouseLeave?.(event);
            }}
            onClick={(event) => {
              targetProps.onClick?.(event);
              props.onClick?.(event);
            }}
            aria-description={props['aria-description'] ?? title}
            aria-describedby={
              [props['aria-describedby'], targetProps['aria-describedby']]
                .filter(Boolean)
                .join(' ') || undefined
            }
            aria-label={
              props['aria-label'] ?? (!props.children && !props.text ? title : undefined)
            }
            ref={(element) => {
              if (typeof ref === 'function') ref(element);
              else if (ref) ref.current = element;
              if (typeof forwardedRef === 'function') forwardedRef(element);
              else if (forwardedRef) forwardedRef.current = element;
            }}
          />
        )}
      />
    );
  },
);
